/**
 * B-050 — the benchmark lines on the two performance charts: AgentsPage's "Return % vs Market" and the
 * agent detail chart (NavChart). SPY and QQQ, plus the income funds JEPI, QYLD and YMAX (owner, Oct 6,
 * 2026: "I need JEPI, and others in the chart"), each a dashed total-return line with its legend value.
 *
 * Basis: GET /benchmarks serves all five from the stored dividend-adjusted closes the Scoreboard reads
 * (benchmark_history, B-043), never from MarketData on a page view. A legend value whose closing point is
 * still pending carries "*", as on the Scoreboard. Windows (follow-up, Product, Oct 6): "1 Year" starts at the
 * platform's first NAV, and YTD at each series' prior-year close, as on the Scoreboard.
 */

/** One served close: `price` is the stored dividend-adjusted close, `provisional` marks a pending one. */
export interface BenchmarkPoint {
  date: string;
  price: number;
  return_pct?: number;
  provisional?: boolean;
}

export interface Benchmark {
  symbol: string;
  /**
   * Line, swatch and legend colour: no agent line's colour and not the gain/loss green or red (NavChart draws
   * the agent green or red). QQQ was Agent Viper's violet until the B-050 follow-up; it is now fuchsia, the
   * Tailwind shade farthest from every other line (CIEDE2000 >= 16, like YMAX vs Agent Shield's pink).
   */
  color: string;
  /** The chart tooltip's name for the line. */
  label: string;
}

export const BENCHMARKS: ReadonlyArray<Benchmark> = [
  { symbol: 'SPY', color: '#facc15', label: '📊 SPY' },
  { symbol: 'QQQ', color: '#d946ef', label: '📈 QQQ' },
  { symbol: 'JEPI', color: '#e2e8f0', label: 'JEPI' },
  { symbol: 'QYLD', color: '#a8a29e', label: 'QYLD' },
  { symbol: 'YMAX', color: '#f0abfc', label: 'YMAX' },
];

/** The symbols both charts request from GET /benchmarks. */
export const BENCHMARK_SYMBOLS: string[] = BENCHMARKS.map((b) => b.symbol);

/** Every benchmark line is dashed; agent lines are solid. */
export const BENCHMARK_DASH = '6 3';

export interface WindowReturn {
  /** Return in %, from the base close to the end close. */
  pct: number;
  /** The end close is still pending (provisional): its legend value carries "*". */
  pending: boolean;
  /** The end close's date: the legend names it when the chart runs past it (the funds refresh at 5 PM ET). */
  end: string;
}

const usable = (p: BenchmarkPoint | null | undefined): p is BenchmarkPoint =>
  !!p && typeof p.date === 'string' && typeof p.price === 'number' && Number.isFinite(p.price) && p.price > 0;

/**
 * One served series' return over a chart's window, from its stored closes: the close on or before
 * `start` (the series' first close when none is that early) to the close on or before `end` (its last
 * close when `end` is omitted). null when the window holds no two distinct closes, or a close is not a
 * positive number: never a 0 standing in for "unknown".
 */
export const windowReturn = (
  points: BenchmarkPoint[] | null | undefined,
  start?: string,
  end?: string,
): WindowReturn | null => {
  if (!Array.isArray(points)) return null;
  if (!points.every(usable)) return null;
  const sorted = [...points].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  let baseIdx = 0;
  if (start !== undefined) sorted.forEach((p, i) => { if (p.date <= start) baseIdx = i; });
  let endIdx = sorted.length - 1;
  if (end !== undefined) {
    endIdx = -1;
    sorted.forEach((p, i) => { if (p.date <= end) endIdx = i; });
  }
  if (endIdx <= baseIdx) return null;
  const base = sorted[baseIdx];
  const last = sorted[endIdx];
  return { pct: (last.price / base.price - 1) * 100, pending: last.provisional === true, end: last.date };
};

/** The served series for `symbol`, or [] when the response has none (a `_failed` symbol is served as []). */
export const seriesOf = (data: unknown, symbol: string): BenchmarkPoint[] => {
  const v = data && typeof data === 'object' ? (data as Record<string, unknown>)[symbol] : undefined;
  return Array.isArray(v) ? (v as BenchmarkPoint[]) : [];
};
